import Config from "../../config"
import router from "../../router"
import authenticatedAxios from "../../authenticated-axios"

// authenticatedAxios.defaults.withCredentials = true

// Keycloak endpoints
const tokenEndpoint = `${Config.auth.keycloakURL}/realms/${Config.auth.realm}/protocol/openid-connect/token`
const logoutEndpoint = `${Config.auth.keycloakURL}/realms/${Config.auth.realm}/protocol/openid-connect/logout`

// Save tokens to sessionStorage
function saveTokensToStorage({ access_token, refresh_token, expires_in }) {
    const expiresAt = Date.now() + expires_in * 1000
    sessionStorage.setItem("access_token", access_token)
    sessionStorage.setItem("refresh_token", refresh_token)
    sessionStorage.setItem("expires_at", expiresAt)
}

// Clear tokens from sessionStorage
function clearTokensFromStorage() {
    sessionStorage.removeItem("access_token")
    sessionStorage.removeItem("refresh_token")
    sessionStorage.removeItem("expires_at")
}

// Check if token expired or close to expiry
function isTokenExpired(expiresAt, minValidity = 30) {
  return Date.now() > (expiresAt - minValidity * 1000)
}

const auth = {
    namespaced: true,
    state: {
        accessToken: sessionStorage.getItem("access_token") || "",
        refreshToken: sessionStorage.getItem("refresh_token") || "",
        expiresAt: Number(sessionStorage.getItem("expires_at")) || 0,
        userInfo: null,
        errorMessage: null,
        authenticated: false,
        minValidity: 30,
        refreshTimer: null,
        ready: false
    },
    getters: {
        isAuthenticated: (state) => state.authenticated && !!state.accessToken,
        getUsername: (state) => state.userInfo?.preferred_username || null,
        getErrorMessage: (state) => state.errorMessage,
    },
    mutations: {
        setReady(state, value) {
            state.ready = value
        },
        setTokens(state, { access_token, refresh_token, expires_in }) {
            state.accessToken = access_token
            state.refreshToken = refresh_token
            state.expiresAt = Date.now() + expires_in * 1000
            state.authenticated = true
            saveTokensToStorage({ access_token, refresh_token, expires_in })
        },
        setUserInfo(state, userInfo) {
            state.userInfo = userInfo
        },
        clearAuth(state) {
            state.accessToken = ""
            state.refreshToken = ""
            state.expiresAt = 0
            state.userInfo = null
            state.authenticated = false
            state.errorMessage = null
            clearTokensFromStorage()
            delete authenticatedAxios.defaults.headers.common['Authorization']
            if(state.refreshTimer) {
                clearInterval(state.refreshTimer)
                state.refreshTimer = null
            }
        },
        setError(state, message) {
            state.errorMessage = message
        },
        clearError(state) {
            state.errorMessage = null
        },
        setRefreshTimer(state, timer) {
            state.refreshTimer = timer
        }
    },
    actions: {
        async validateLogin({ commit, dispatch }, payload) {
            commit('clearError')

            const params = new URLSearchParams()
            params.append('grant_type', 'password')
            params.append('client_id', Config.auth.clientId)
            params.append('username', payload.username)
            params.append('password', payload.password)

            try {
                const response = await authenticatedAxios.post(tokenEndpoint, params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                })
                commit('setTokens', response.data)
                await dispatch('loadUserInfo')
                dispatch('startTokenRefreshTimer')
                return true
            } catch (error) {
                if (error.response?.status === 401) {
                    commit('setError', 'Invalid Credentials!')
                } else if (error.response?.status === 403) {
                    commit('setError', 'Invalid Session!')
                } else {
                    commit('setError', 'Login failed')
                }
                throw error
            }
        },
        async loadUserInfo({ commit, state }) {
            try {
                const base64Payload = state.accessToken.split('.')[1]
                const payload = JSON.parse(atob(base64Payload))
                commit('setUserInfo', payload)
            } catch {
                try {
                    const response = await authenticatedAxios.get(`${Config.auth.keycloakURL}/realms/${Config.auth.realm}/protocol/openid-connect/userinfo`)
                    console.log("userInfo response: ", response)
                    commit('setUserInfo', response.data)
                } catch (error) {
                    commit('setError', 'Failed to load user info')
                }
            }
        },

        async refreshToken({ commit, state, dispatch }) {
            if (!state.refreshToken) {
                commit('clearAuth')
                return
            }

            if (!isTokenExpired(state.expiresAt, state.minValidity)) {
                return
            }

            const params = new URLSearchParams()
            params.append('grant_type', 'refresh_token')
            params.append('client_id', Config.auth.clientId)
            params.append('refresh_token', state.refreshToken)

            try {
                const response = await authenticatedAxios.post(tokenEndpoint, params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                })
                commit('setTokens', response.data)
                await dispatch('loadUserInfo')
            } catch (error) {
                commit('clearAuth')
                router.push({ name: 'login' })
            }
        },

        startTokenRefreshTimer({ commit, dispatch, state }) {
            if (state.refreshTimer) clearInterval(state.refreshTimer)

            const timer = setInterval(() => {
                dispatch('refreshToken')
            }, 60000) // check every 60 seconds

            commit('setRefreshTimer', timer)
        },

        async logout({ commit, state }) {
            try {
                const params = new URLSearchParams()
                params.append('client_id', Config.auth.clientId)
                params.append('refresh_token', state.refreshToken)
                const logoutUrl = `${logoutEndpoint}?${params.toString()}`
                await authenticatedAxios.get(logoutUrl)
            } catch (error) {
                console.warn('Logout error', error)
            } finally {
                commit('clearAuth')
            }
        },

        updateEmail({ state, commit }, email) {
            const updated = { ...state.userInfo, email }
            commit('setUserInfo', updated)
        },

        async init({ commit, dispatch }) {
            const accessToken = sessionStorage.getItem('access_token')
            const refreshToken = sessionStorage.getItem('refresh_token')
            const expiresAt = Number(sessionStorage.getItem('expires_at'))

            if (accessToken && refreshToken && expiresAt) {
                if (isTokenExpired(expiresAt)) {
                    commit('clearAuth')
                } else {
                    commit('setTokens', {
                        access_token: accessToken,
                        refresh_token: refreshToken,
                        expires_in: (expiresAt - Date.now()) / 1000,
                    })
                }
                await dispatch('loadUserInfo')
                dispatch('startTokenRefreshTimer')
            }

            commit('setReady', true)
        }
    }
}

export default auth
